<template>
    <CustomModal :modalId="modalId" :isOpen="isOpen" @modalClosed="closeModal" :showFooter="false">
        <template #header>
            <h5 class="modal-title">{{ $t('scan_invoice') }}</h5>
            <button type="button" class="btn-close" @click="closeModal"></button>
        </template>
        <template #body>
            <div class="p-4">
                <div
                    class="upload-area p-5 text-center border rounded-3 bg-light position-relative"
                    @dragover.prevent="isDragging = true"
                    @dragleave.prevent="isDragging = false"
                    @drop.prevent="handleDrop"
                    @click="triggerFileInput"
                    :class="{ 'border-primary': isDragging, 'bg-white': isDragging }"
                    style="border-style: dashed !important; cursor: pointer; transition: all 0.2s ease;"
                >
                    <input
                        type="file"
                        ref="fileInput"
                        class="d-none"
                        accept="image/*,.pdf,.docx,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                        @change="handleFileSelect"
                    >
                    
                    <div v-if="scanning" class="text-center">
                        <div class="spinner-border text-primary mb-3" role="status" style="width: 3rem; height: 3rem;">
                            <span class="visually-hidden">Loading...</span>
                        </div>
                        <h5 class="text-muted">{{ $t('scanning_invoice') }}...</h5>
                        <p class="text-muted small mb-0">{{ $t('please_wait_extracting_data') }}</p>
                    </div>
                    
                    <div v-else>
                        <AppIcon name="bi-cloud-upload" class="bi bi-cloud-upload text-primary mb-3" style="font-size: 3rem;" />
                        <h5 class="mb-2">{{ $t('drag_drop_invoice') }}</h5>
                        <p class="text-muted mb-0">{{ $t('or_click_to_browse') }}</p>
                        <p class="text-muted small mt-2 mb-0">PDF, DOCX, JPG, PNG</p>
                    </div>
                </div>
            </div>
        </template>
    </CustomModal>
</template>

<script>
import CustomModal from '../CustomModal.vue'
import helper from '../../helper'
import { convertInvoiceFileToImage, isSupportedInvoiceFile } from '../../utils/invoiceFile'

export default {
    name: 'ScanInvoiceModal',
    components: {
        CustomModal
    },
    props: {
        isOpen: {
            type: Boolean,
            required: true
        },
        modalId: {
            type: String,
            default: 'scan-invoice-modal'
        }
    },
    data() {
        return {
            isDragging: false,
            scanning: false
        }
    },
    methods: {
        closeModal() {
            if (!this.scanning) {
                this.$emit('close');
            }
        },
        triggerFileInput() {
            if (!this.scanning) {
                this.$refs.fileInput.click();
            }
        },
        handleFileSelect(event) {
            const file = event.target.files[0];
            if (file) {
                this.processFile(file);
            }
        },
        handleDrop(event) {
            this.isDragging = false;
            if (this.scanning) return;
            
            const file = event.dataTransfer.files[0];
            if (file) {
                this.processFile(file);
            }
        },
        async processFile(file) {
            this.scanning = true;
            
            try {
                if (!isSupportedInvoiceFile(file)) throw new Error('Unsupported file type.');
                const base64 = await convertInvoiceFileToImage(file);

                const response = await helper.customPost(`${process.env.VUE_APP_API}/supplier-invoices/scan`, { image: base64 });
                this.scanning = false;
                this.$emit('scan-success', { ...response.data, scannedFile: file });
                this.closeModal();

            } catch (error) {
                this.$swal.fire({
                    icon: 'error',
                    title: 'Scan Failed',
                    text: error.message || 'Could not scan the invoice. Please try again.'
                });
            } finally {
                this.scanning = false;
                if (this.$refs.fileInput) {
                    this.$refs.fileInput.value = '';
                }
            }
        }
    },
    beforeDestroy() {
        // Ensure modal backdrop is removed when component is destroyed
        const backdrops = document.querySelectorAll('.modal-backdrop');
        backdrops.forEach(backdrop => backdrop.remove());
        document.body.classList.remove('modal-open');
        document.body.style.paddingRight = '';
    }
}
</script>

<style scoped>
.upload-area:hover {
    border-color: #0d6efd !important;
    background-color: #f8f9fa !important;
}
</style>
